import { Button, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { messageFor } from "../../error-copy";
import {
  CHECK_KIND,
  type CheckKind,
  type ChecklistItem,
  checklistActions,
  moved,
  shiftChecklistQuery,
} from "../../fleet";
import { ConfirmDialog } from "../shared/confirm";
import styles from "./settings.module.css";

const KINDS = Object.keys(CHECK_KIND) as CheckKind[];

/**
 * Shift checklist (P8-36): what a driver answers in the driver app to open a shift, in order, and
 * whether they must name the assistant working with them. Drivers see a change on their next
 * shift; past shifts keep the answers they gave.
 */
export function ShiftChecklistPage(): ReactNode {
  const queryClient = useQueryClient();
  const checklist = useQuery(shiftChecklistQuery);
  const [editing, setEditing] = useState<ChecklistItem | "new" | null>(null);
  const [removing, setRemoving] = useState<ChecklistItem | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: shiftChecklistQuery.queryKey });
  const assistant = useMutation({ mutationFn: checklistActions.requireAssistant, onSuccess: refresh });
  const reorder = useMutation({ mutationFn: checklistActions.reorder, onSuccess: refresh });

  const header = (
    <PageHeader
      title="Shift checklist"
      description="What a driver answers in the driver app to start a shift: photos, checks, readings and notes."
      actions={<Button onClick={() => setEditing("new")}>Add an item</Button>}
    />
  );
  if (checklist.status === "pending")
    return (
      <>
        {header}
        <PageLoading label="Loading the checklist" />
      </>
    );
  if (checklist.status === "error")
    return (
      <>
        {header}
        <PageError message={messageFor(errorOf(checklist.error))} onRetry={() => checklist.refetch()} />
      </>
    );
  const items = checklist.data.items;
  const error = assistant.error ?? reorder.error;
  return (
    <>
      {header}
      <div className={styles.panel}>
        <Switch
          label="Drivers name their assistant"
          checked={checklist.data.requires_assistant}
          disabled={assistant.isPending}
          onCheckedChange={(on) => assistant.mutate(on)}
        />
        <p className={styles.muted}>
          With this on, a driver picks the assistant working with them today, from their own fleet, to start a shift.
        </p>
        {error ? (
          <Alert tone="danger" title="Not saved">
            {messageFor(errorOf(error))}
          </Alert>
        ) : null}
        <SortableDataTable
          caption="Checklist items, in the order drivers see them"
          rowKey="id"
          rows={items}
          itemName={{ one: "item", other: "items" }}
          emptyMessage="No checklist: drivers start a shift by picking their truck. Add items to ask for more."
          columns={[
            {
              key: "label",
              label: "Item",
              sortable: false,
              render: (_v, i) => (
                <span>
                  {i.label}
                  {i.help_text ? <span className={styles.muted}> · {i.help_text}</span> : null}
                </span>
              ),
            },
            { key: "kind", label: "Answer", sortable: false, render: (_v, i) => CHECK_KIND[i.kind] },
            {
              key: "required",
              label: "Required",
              sortable: false,
              render: (_v, i) =>
                i.required ? <Badge tone="info">Required</Badge> : <Badge tone="neutral">Optional</Badge>,
            },
            {
              key: "actions",
              label: "",
              sortable: false,
              render: (_v, i) => {
                const at = items.indexOf(i);
                return (
                  <div className={styles.actions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={at === 0 || reorder.isPending}
                      aria-label={`Move ${i.label} up`}
                      onClick={() => reorder.mutate(moved(items, i.id, -1))}
                    >
                      Up
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={at === items.length - 1 || reorder.isPending}
                      aria-label={`Move ${i.label} down`}
                      onClick={() => reorder.mutate(moved(items, i.id, 1))}
                    >
                      Down
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(i)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setRemoving(i)}>
                      Remove
                    </Button>
                  </div>
                );
              },
            },
          ]}
        />
        <p className={styles.muted}>
          A required yes-or-no item has to be answered yes to start the shift. Changes apply from each driver's next
          shift; past shifts keep the answers they gave.
        </p>
      </div>
      {editing ? <ItemDialog item={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} /> : null}
      {removing ? (
        <ConfirmDialog
          title={`Remove ${removing.label}?`}
          description="Drivers stop seeing it from their next shift. Past shifts keep their answers."
          action="Remove"
          danger
          run={() => checklistActions.archiveItem(removing.id)}
          onDone={refresh}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}

/** Add an item, or edit one. Once drivers have answered an item, its kind stays as it is. */
function ItemDialog({ item, onClose }: { item?: ChecklistItem; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(item?.label ?? "");
  const [help, setHelp] = useState(item?.help_text ?? "");
  const [kind, setKind] = useState<CheckKind>(item?.kind ?? "photo");
  const [required, setRequired] = useState(item?.required ?? true);
  const mutation = useMutation({
    mutationFn: () => {
      const body = { label: label.trim(), help_text: help.trim() || null, kind, required };
      return item ? checklistActions.updateItem(item.id, body) : checklistActions.createItem(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shiftChecklistQuery.queryKey });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={item ? `Edit ${item.label}` : "Add a checklist item"}>
        <div className={styles.form}>
          <Input
            label="What to ask"
            value={label}
            maxLength={80}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Selfie with your assistant"
          />
          <Input
            label="Help for the driver"
            value={help}
            maxLength={300}
            onChange={(e) => setHelp(e.target.value)}
            placeholder="Both faces in the picture, in front of the truck"
            description="Optional."
          />
          <Select
            label="Answer"
            value={kind}
            onValueChange={(v) => setKind(v as CheckKind)}
            options={KINDS.map((k) => ({ value: k, label: CHECK_KIND[k] }))}
          />
          <Switch label="Required to start a shift" checked={required} onCheckedChange={setRequired} />
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!label.trim()} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {item ? "Save" : "Add item"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
